import { Alert, Card, Grid, MultiSelect, SegmentedControl, Text } from '@mantine/core';
import { GA_TRACKING_EVENTS, GA_TRACKING_EVENT_NAMES } from '../../../../constants/googleTrackingEvents';
import OptionsSelect from '../../../OptionsSelect';

const SettingsTabGoogleAnalytics = ({ googleAnalyticsAccount, handleChange, linkButton }) => {
  const backendTracking = googleAnalyticsAccount?.backendTracking ?? false;
  const enabledTrackingEvents = googleAnalyticsAccount?.enabledTrackingEvents || [];

  return (
    <Card withBorder>
      <Card.Section p="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Analytics Account*</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <OptionsSelect
              name="account"
              endpoint="googleAnalytics/accounts"
              handleChange={handleChange}
              value={googleAnalyticsAccount?.accountId || ''}
            />
          </Grid.Col>
        </Grid>
        {googleAnalyticsAccount?.accountId && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Property*</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <OptionsSelect
                name="property"
                endpoint={`googleAnalytics/properties/${googleAnalyticsAccount.accountId}`}
                handleChange={handleChange}
                text="The Google Analytics property linked to your Google Ads account."
                value={googleAnalyticsAccount.propertyId || ''}
              />
            </Grid.Col>
          </Grid>
        )}
        {googleAnalyticsAccount?.propertyId && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Link Property to Google Ads*</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              {linkButton}
              <Text size="xs" c="dimmed" mt="xs">
                <strong>
                  To link a GA property to a Google Ads account, you must have "edit" permissions on the GA property and
                  admin access on the Google Ads account.
                </strong>
              </Text>
            </Grid.Col>
          </Grid>
        )}
        {googleAnalyticsAccount?.propertyId && googleAnalyticsAccount?.googleAdsLinkId && (
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Import Google Analytics audiences*</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <SegmentedControl
                value={String(googleAnalyticsAccount?.adsPersonalizationEnabled || false)}
                onChange={(value) =>
                  handleChange({ target: { name: 'adsPersonalizationEnabled', value: value === 'true' } })
                }
                data={[
                  { label: 'OFF', value: 'false' },
                  { label: 'ON', value: 'true' },
                ]}
              />
              <Text size="xs" c="dimmed" mt="xs">
                <strong>Must be turned ON</strong> to publish your Google Analytics audiences to the linked Google Ads
                account
              </Text>
            </Grid.Col>
          </Grid>
        )}
        {googleAnalyticsAccount?.propertyId && (
          <>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Data Stream*</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                <OptionsSelect
                  name="dataStream"
                  endpoint={`googleAnalytics/propertyDataStreams/${googleAnalyticsAccount.propertyId}`}
                  handleChange={handleChange}
                  text="The Google Analytics Data Stream where events will be send"
                  value={googleAnalyticsAccount.dataStreamId || ''}
                />
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Backend Tracking</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                {/* Read-only for now: flipping this is a Releva-staff decision
                    made together with the event review below, so it is set in
                    the database directly rather than exposed to operators. */}
                <SegmentedControl
                  value={String(backendTracking)}
                  onChange={(value) => handleChange({ target: { name: 'backendTracking', value: value === 'true' } })}
                  data={[
                    { label: 'OFF', value: 'false' },
                    { label: 'ON', value: 'true' },
                  ]}
                  disabled={true}
                />
                <Text size="xs" c="dimmed" mt="xs">
                  Send user behavior to your Google Analytics for better ad performance. Managed by Releva - contact
                  support to change it.
                </Text>
              </Grid.Col>
            </Grid>
            <Grid mb="md">
              <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
                <label>Tracked events</label>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 8 }}>
                {/* The duplication warning is only actionable while the picker
                    is editable; with Backend Tracking off it would sit above a
                    permanently disabled control an operator cannot act on. */}
                {backendTracking && (
                  <Alert color="yellow" mb="xs" title="Events can be duplicated">
                    Events will be duplicated in Google Analytics if they are sent both from your website and from
                    Releva&apos;s servers. Enable only events that your website does not already send.
                  </Alert>
                )}
                <MultiSelect
                  data={GA_TRACKING_EVENT_NAMES}
                  value={enabledTrackingEvents}
                  onChange={(value) => handleChange({ target: { name: 'enabledTrackingEvents', value } })}
                  placeholder="Pick the GA4 events Releva should send..."
                  disabled={!backendTracking}
                  searchable
                  clearable
                />
                <Text size="xs" c="dimmed" mt="xs">
                  {!backendTracking && (
                    <strong>Available once Backend Tracking is enabled - contact support to enable it. </strong>
                  )}
                  Releva sends {GA_TRACKING_EVENTS.map((event) => `${event.value} on ${event.source}`).join(', ')}.
                  Campaign and segment entries are always sent and are not listed here, since nothing else reports them.
                </Text>
              </Grid.Col>
            </Grid>
          </>
        )}
      </Card.Section>
    </Card>
  );
};

export default SettingsTabGoogleAnalytics;
